import Fuse, { type IFuseOptions } from 'fuse.js';
import type { Command } from '../types';

/**
 * Fuse.js-based scoring alternative to the hand-tuned `scoreCommand`.
 *
 * The same fields tracked by the manual scorer are used as Fuse keys,
 * with weights that mirror the relative priority used there:
 *
 *  label       – highest weight  (label exact/starts-with scores up to 150)
 *  keywords    – second tier     (keyword exact match scores up to  60)
 *  description – third tier      (description exact match scores up to 80)
 *  category    – lowest weight   (category exact match scores up to  25)
 *
 * Fuse returns a `score` in [0, 1] where 0 is a perfect match.
 * We invert it so that higher = better (matching the manual scorer convention)
 * and scale to a comparable range so `minScore` defaults still work.
 */
const FUSE_OPTIONS: IFuseOptions<Command> = {
  includeScore: true,
  // // Allow slightly fuzzy matching while still preferring exact hits
  // threshold: 0.4,
  // // Search from the start of the string first
  // ignoreLocation: false,
  // location: 0,
  // distance: 60,
  // 0.0 = exact only, 1.0 = match anything.
  // 0.45 allows ~1-2 character typos (transpositions, substitutions, insertions)
  threshold: 0.45,
  // Don't penalise matches that appear later in the string
  ignoreLocation: true,
  minMatchCharLength: 1,
  useExtendedSearch: false,
  keys: [
    { name: 'label',       weight: 0.50 },
    { name: 'keywords',    weight: 0.25 },
    { name: 'description', weight: 0.20 },
    { name: 'category',    weight: 0.05 },
  ],
};

/** Scale factor so fuse scores live in a similar numeric range to the manual scorer. */
const SCALE = 200;

/**
 * Score a command using Fuse.js fuzzy search.
 *
 * Mirrors the signature and return semantics of `scoreCommand`:
 * - Returns 1 when there is no search query (show everything)
 * - Returns 0 when Fuse finds no match above the threshold
 * - Returns a positive number (higher = better match)
 * - Respects `command.scoreOverride` exactly as the manual scorer does
 *
 * @param command     - The command to score
 * @param searchValue - The search query (case-insensitive)
 * @returns A numeric score (higher is better), or 0 if no match
 */
export function scoreCommandFuse(command: Command, searchValue: string): number {
  if (!searchValue?.trim()) {
    return 1;
  }

  // scoreOverride short-circuits everything, same as the manual scorer
  if (command.scoreOverride !== undefined) {
    return command.scoreOverride;
  }

  // Build a single-item Fuse instance for this command
  const fuse = new Fuse([command], FUSE_OPTIONS);
  const results = fuse.search(searchValue.trim());

  if (results.length === 0 || results[0].score === undefined) {
    return 0;
  }

  // Fuse score: 0 = perfect, 1 = no match.  Invert and scale.
  return (1 - results[0].score) * SCALE;
}

/**
 * Filter and sort commands based on Fuse.js search relevance.
 *
 * Drop-in replacement for `filterAndScoreCommands` from scoring.ts.
 *
 * @param commands         - Array of commands to filter and sort
 * @param searchValue      - The search query
 * @param minScore         - Minimum score required to include a command (default: 1)
 * @param optionsOverride  - Optional partial Fuse options to merge with defaults (useful for live tuning)
 * @returns Filtered and sorted array of commands with their scores
 */
export function filterAndScoreCommandsFuse(
  commands: Command[],
  searchValue: string,
  minScore: number = 1,
  optionsOverride?: Partial<IFuseOptions<Command>>,
): Array<{ command: Command; score: number }> {
  if (!searchValue?.trim()) {
    return commands.map(command => ({ command, score: 1 }));
  }

  // Run Fuse over the whole collection at once (much more efficient than
  // building a new instance per command like scoreCommandFuse does).
  const opts = optionsOverride ? { ...FUSE_OPTIONS, ...optionsOverride } : FUSE_OPTIONS;
  const fuse = new Fuse(commands, opts);
  const results = fuse.search(searchValue.trim());

  const scored = results
    .map(result => {
      const command = result.item;
      // Honour scoreOverride even inside bulk search
      const score =
        command.scoreOverride !== undefined
          ? command.scoreOverride
          : (1 - (result.score ?? 1)) * SCALE;
      return { command, score };
    })
    .filter(({ score }) => score >= minScore)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.command.label.localeCompare(b.command.label);
    });

  return scored;
}

/**
 * Group commands by category and sort by Fuse.js score within each group.
 *
 * Drop-in replacement for `groupAndScoreCommands` from scoring.ts.
 *
 * @param commands    - Array of commands to group
 * @param searchValue - The search query
 * @returns Record of category names to sorted command arrays
 */
export function groupAndScoreCommandsFuse(
  commands: Command[],
  searchValue: string,
): Record<string, Command[]> {
  const scored = filterAndScoreCommandsFuse(commands, searchValue);
  const groups: Record<string, Command[]> = {};

  for (const { command } of scored) {
    const category = command.category || 'Other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(command);
  }

  return groups;
}
